import { DesignSystem } from '@/src/design-system'
import { H6, Paragraph, Separator, View, YStack, XStack, H4, useEvent } from 'tamagui'
import Animated, { FadeIn, FadeInRight, FadeOut } from 'react-native-reanimated'
import { useMemo, useRef, useState } from 'react'
import ChevronDown from '@/src/assets/icons/ChevronDown'
import { PurchaseForm } from './PurchaseForm'
import { RefinanceForm } from './RefinanceForm'
import * as Branding from '@/src/experiences/branding'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useSegmentDomain } from '@/src/services/segment/SegmentDomainProvider'

const AnimatedYStack = Animated.createAnimatedComponent(YStack)

export function PointsCalculatorCard() {
  // We only want to track interaction once and not on every Slider event as it's too noisy
  const hasFiredInteractionEventRef = useRef(false)
  const { colorValue: primaryColor, token: primaryColorToken } = Branding.Hooks.usePrimaryColor()
  const { triggerAction } = useSegmentDomain()
  const [selectedTab, setSelectedTab] = useState<'purchase' | 'refinance'>('purchase')
  const [purchaseAmount, setPurchaseAmount] = useState(500_000)
  const [downPaymentAmount, setDownPaymentAmount] = useState(20)
  const [refinanceAmountString, setRefinanceAmountString] = useState('400000')
  const points = useMemo(() => {
    if (Number.isNaN(Number(refinanceAmountString))) {
      return null
    }

    if (selectedTab === 'purchase') {
      return purchaseAmount * (1 - downPaymentAmount / 100)
    }

    return Number(refinanceAmountString)
  }, [selectedTab, purchaseAmount, downPaymentAmount, refinanceAmountString])

  const trackInteractionEvent = useEvent(() => {
    if (!hasFiredInteractionEventRef.current) {
      hasFiredInteractionEventRef.current = true
      void triggerAction('PointsCalculatorCard', 'Interacted', {
        tab: selectedTab,
      })
    }
  })

  const handleChangePurchaseAmount = useEvent(
    (purchaseAmount: number, downPaymentAmount: number) => {
      setPurchaseAmount(purchaseAmount)
      setDownPaymentAmount(downPaymentAmount)
      trackInteractionEvent()
    },
  )

  const handleChangeRefinanceAmountString = useEvent((refinanceAmountString: string) => {
    setRefinanceAmountString(refinanceAmountString)
    trackInteractionEvent()
  })

  const handleChangeSelectedTab = useEvent((selectedTab: 'purchase' | 'refinance') => {
    setSelectedTab(selectedTab)
    void triggerAction('PointsCalculatorCard', `SwitchTab_${selectedTab}`)

    // Track if they interact after the tab is changed
    hasFiredInteractionEventRef.current = false
  })

  return (
    <DesignSystem.Card>
      <YStack gap="$2" px="$2" overflow="hidden">
        <H6>Buy or Refinance, Get Rewarded</H6>
        <Paragraph>
          See how many Mesa Points you could earn when you buy or refinance with Mesa Mortgage.
        </Paragraph>
        <Separator my="$3" />
        <View flexDirection="row" mb="$4">
          <DesignSystem.Chip
            label="Purchase"
            selected={selectedTab === 'purchase'}
            onPress={() => handleChangeSelectedTab('purchase')}
            containerStyle={{
              flex: 1,
              borderColor: primaryColor,
              backgroundColor: selectedTab === 'purchase' ? primaryColor : 'transparent',
            }}
          />
          <DesignSystem.Chip
            label="Refinance"
            selected={selectedTab === 'refinance'}
            onPress={() => handleChangeSelectedTab('refinance')}
            containerStyle={{
              flex: 1,
              borderColor: primaryColor,
              backgroundColor: selectedTab === 'refinance' ? primaryColor : 'transparent',
            }}
          />
        </View>
        {selectedTab === 'purchase' && (
          <Animated.View entering={FadeInRight}>
            <PurchaseForm
              purchaseAmount={purchaseAmount}
              downPaymentAmount={downPaymentAmount}
              onChange={handleChangePurchaseAmount}
            />
          </Animated.View>
        )}
        {selectedTab === 'refinance' && (
          <Animated.View entering={FadeInRight}>
            <RefinanceForm
              refinanceAmountString={refinanceAmountString}
              onChange={handleChangeRefinanceAmountString}
            />
          </Animated.View>
        )}
        {points ? (
          <AnimatedYStack entering={FadeIn} exiting={FadeOut}>
            <XStack self="center" gap="$1" mt="$2">
              <ChevronDown />
            </XStack>
            <YStack self="center">
              <H4 color={primaryColorToken} text="center">
                {points.toLocaleString()} Mesa Points
              </H4>
              <View height="$1" />
              <DesignSystem.Markdown
                style={{
                  body: {
                    color: mesaColorNames['Greyscale/400 #A4AAAC'],
                    ...typography.XS,
                  },
                  link: {
                    ...typography.XS,
                  },
                }}
              >
                Point rewards are subject to [terms and
                conditions](https://www.mesamember.com/legal/mesa-membership-terms-and-conditions),
                and not all borrowers will qualify. Points awarded after closing. This calculator is
                an estimate only.
              </DesignSystem.Markdown>
            </YStack>
          </AnimatedYStack>
        ) : null}
      </YStack>
    </DesignSystem.Card>
  )
}
